// src/antdviews/Sidebar.tsx

// 侧边栏导航组件 - Ant Design 重构版

import React from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { Layout, Menu, Button, Tooltip, Typography } from 'antd'
import type { MenuProps } from 'antd'
import {
    TeamOutlined,
    ArrowUpOutlined,
    ArrowLeftOutlined,
    ArrowDownOutlined,
    ArrowRightOutlined,
    DotChartOutlined,
    CalendarOutlined,
    InfoCircleOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    SettingOutlined,
} from '@ant-design/icons'
import { FUNCTION } from '../../services/Macro'
import { Page } from '../../services/Page'
import { ROOT } from '../../services/Api'

const { Sider } = Layout
const { Text } = Typography

// 定义菜单项类型
type MenuItem = Required<MenuProps>['items'][number]

interface SidebarProps extends WithTranslation {
    selectMenu?: string
    trans: (key: string) => string
    collapsed?: boolean
    onCollapse?: (collapsed: boolean) => void
}

interface SidebarState {
    collapsed: boolean
}

class Sidebar extends React.Component<SidebarProps, SidebarState> {

    state: SidebarState = {
        collapsed: false,
    }

    toggleSidebar = () => {
        console.log(`开关侧边栏`)
        const newCollapsed = !this.state.collapsed
        this.setState({ collapsed: newCollapsed })
        if (this.props.onCollapse) {
            this.props.onCollapse(newCollapsed)
        }
    }

    /** 菜单点击跳转 */
    handleMenuClick = (path: string) => {
        window.location.href = `${ROOT}/${path}`
    }

    /** 构建菜单项配置 - 返回 MenuProps 的 items 格式 */
    getMenuItems = (): MenuItem[] => {
        const { trans } = this.props
        const items: MenuItem[] = []

        if (FUNCTION.ACCOUNT_MGMT) {
            items.push({
                key: Page.accountMgmt,
                icon: <TeamOutlined />,
                label: trans('accountMgmt'),
                onClick: () => this.handleMenuClick(Page.accountMgmt),
            })
        }

        if (FUNCTION.UPGRADE_MGMT) {
            items.push({
                key: Page.firmware,
                icon: <ArrowUpOutlined />,
                label: trans('upgradeMgmt'),
                onClick: () => this.handleMenuClick(Page.firmware),
            })
        }

        if (FUNCTION.SYS_MGMT) {
            items.push({
                key: Page.system,
                icon: <SettingOutlined />,
                label: trans('systemMgmt'),
                onClick: () => this.handleMenuClick(Page.system),
            })
        }

        if (FUNCTION.STATS_MGMT) {
            items.push({
                key: Page.stats,
                icon: <ArrowDownOutlined />,
                label: trans('statsMgmt'),
                onClick: () => this.handleMenuClick(Page.stats),
            })
        }

        if (FUNCTION.LOG_MGMT) {
            items.push({
                key: Page.logs,
                icon: <ArrowRightOutlined />,
                label: trans('logsMgmt'),
                onClick: () => this.handleMenuClick(Page.logs),
            })
        }

        if (FUNCTION.DYNAMIC_MGMT) {
            items.push({
                key: Page.dynamic,
                icon: <DotChartOutlined />,
                label: trans('dynamicMgmt'),
                onClick: () => this.handleMenuClick(Page.dynamic),
            })
        }

        if (FUNCTION.CALENDAR_VIEW) {
            items.push({
                key: Page.calendar,
                icon: <CalendarOutlined />,
                label: trans('calendar'),
                onClick: () => this.handleMenuClick(Page.calendar),
            })
        }

        if (FUNCTION.PUSH_MGMT) {
            items.push({
                key: Page.pushMgmt,
                icon: <InfoCircleOutlined />,
                label: trans('pushMgmt'),
                onClick: () => this.handleMenuClick(Page.pushMgmt),
            })
        }

        if (FUNCTION.EDF_TASK) {
            items.push({
                key: Page.edfTask,
                icon: <InfoCircleOutlined />,
                label: trans('edfTask'),
                onClick: () => this.handleMenuClick(Page.edfTask),
            })
        }

        return items
    }

    /** 判断当前选中的菜单项 */
    getSelectedKeys = (): string[] => {
        const { selectMenu } = this.props
        if (selectMenu) {
            const menuItems = this.getMenuItems()
            const hasKey = menuItems.some((item) => item?.key === selectMenu)
            if (hasKey) {
                return [selectMenu]
            }
        }
        return []
    }

    render() {
        const { trans, selectMenu } = this.props
        const { collapsed } = this.state

        const menuItems = this.getMenuItems()

        if (menuItems.length === 0) {
            return (
                <Sider
                    width={200}
                    style={{
                        background: '#fff',
                        boxShadow: '2px 0 8px rgba(0, 21, 41, 0.08)',
                        height: 'calc(100vh - 64px)',
                        position: 'sticky',
                        top: 64,
                        overflow: 'auto',
                    }}
                >
                    <div style={{ padding: '20px', textAlign: 'center', color: '#bfbfbf' }}>
                        <Text type="secondary">{trans('noMenu') || '暂无菜单'}</Text>
                    </div>
                </Sider>
            )
        }

        return (
            <Sider
                width={240}
                collapsed={collapsed}
                collapsedWidth={56}
                style={{
                    background: 'var(--sidebar-bg)',
                    boxShadow: '2px 0 8px rgba(0, 21, 41, 0.08)',
                    height: 'calc(100vh - 64px)',
                    position: 'sticky',
                    top: 64,
                    overflow: 'auto',
                }}
                theme="light"
            >
                {/* 侧边栏头部：标题 + 折叠按钮 */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: collapsed ? '12px 8px' : '16px 20px',
                        borderBottom: '1px solid #f0f0f0',
                        height: 56,
                        transition: 'all 0.2s',
                    }}
                >
                    {!collapsed && (
                        <Text strong style={{ fontSize: 14, color: '#1890ff' }}>
                            {trans('navigation') || 'Navigation'}
                        </Text>
                    )}
                    <Tooltip title={collapsed ? (trans('expand') || '展开') : (trans('collapse') || '收起')}>
                        <Button
                            type="text"
                            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                            onClick={this.toggleSidebar}
                            style={{
                                fontSize: 16,
                                width: 32,
                                height: 32,
                                padding: 0,
                                marginLeft: collapsed ? 0 : 'auto',
                            }}
                        />
                    </Tooltip>
                </div>

                {/* 导航菜单 */}
                <Menu
                    mode="inline"
                    theme="light"
                    selectedKeys={this.getSelectedKeys()}
                    defaultSelectedKeys={selectMenu ? [selectMenu] : []}
                    style={{
                        borderRight: 'none',
                        padding: '8px 0',
                    }}
                    items={menuItems}
                />
            </Sider>
        )
    }
}

export default withTranslation()(Sidebar)